/*
===============================================================================================================
                                 Copyright 2025-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import axios from 'axios';

import { getAdminConfig } from '../static-api/admin-config';
import { getLocalAdminConfig } from '../static-api/local-admin-config';
import { getLocale } from '../static-api/locale';
import { getCompanyStatus } from '../static-api/company-status';
import { getMerchantBrand } from '../static-api/merchant-brand';
import { getMerchantConfig } from '../static-api/merchant-config';
import { isStaticApiEnabled, isStaticBuildEnabled } from './static-build';

/**
* Resolves the api route path to either call a Next.js api or a static client-side function
* @param {string} apiRoute String representing the api-route path
* @param {object} params A configuration object
* @return {Promise} A result of an async function call
 */
export const resolveApiFunction = (apiRoute, params) => {
  let resolvedApiFunction = () => {};

  const shouldUseStaticApi = isStaticBuildEnabled() || isStaticApiEnabled();
  switch (apiRoute) {
    case '/api/admin-config':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getAdminConfig({ carrier: params.carrier, token: params.token });
      } else {
        resolvedApiFunction = axios.get('/api/admin-config', {
          params: {
            carrier: params.carrier,
          },
          headers: {
            Authorization: params.token,
          },
        });
      }
      break;

    case '/api/local-admin-config':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getLocalAdminConfig({ carrier: params.carrier });
      } else {
        resolvedApiFunction = axios.get('/api/local-admin-config', {
          params: {
            carrier: params.carrier,
          },
        });
      }
      break;

    case '/api/company-status':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getCompanyStatus({
          organisationId: params.organisationId,
          token: params.token,
          hostId: params.hostId,
        });
      } else {
        resolvedApiFunction = axios.get('/api/company-status', {
          params: {
            organisationId: params.organisationId,
            hostId: params.hostId,
          },
          headers: {
            Authorization: params.token,
          },
        });
      }
      break;

    case '/api/merchant-brand':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getMerchantBrand({
          merchant: params.merchant, token: params.token,
        });
      } else {
        resolvedApiFunction = axios.post('/api/merchant-brand', {
          params: {
            merchant: params.merchant,
            token: params.token,
          },
        });
      }
      break;

    case '/api/merchant-config':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getMerchantConfig({
          merchant: params.merchant,
          localModeEnabled: params.localModeEnabled,
          hostId: params.hostId,
          consumerUrl: params.consumerUrl,
        });
      } else {
        resolvedApiFunction = axios.post('/api/merchant-config', {
          params: {
            merchant: params.merchant,
            token: params.token,
            localModeEnabled: params.localModeEnabled,
            hostId: params.hostId,
            consumerUrl: params.consumerUrl,
          },
        });
      }
      break;

    case '/api/locale':
      if (shouldUseStaticApi) {
        resolvedApiFunction = getLocale({ locale: params.locale, carrier: params.carrier });
      } else {
        resolvedApiFunction = axios.get(`/api/locale?locale=${params.locale}&carrier=${params.carrier}`);
      }
      break;

    default:
      break;
  }

  return resolvedApiFunction;
};

export default resolveApiFunction;
